/**
 * Title normalization, pre-fill decision logic.
 *
 * Pure helpers extracted from `UploadModal` so the pre-fill rules can be
 * unit-tested in isolation (the test runner is a node env, no jsdom).
 *
 * Spec : docs/spec/title-normalization-spec.md §4 (thresholds) + §8 (anti-stomp).
 */

import type {
  InspectMidiResponse,
  TitleSuggestionSource,
} from "@/lib/music/analyzer-types";

/** Minimum confidence below which we never pre-fill, keeps the slug humanization. */
export const TITLE_PREFILL_MIN_CONFIDENCE = 0.4;

/** Confidence at and above which the suggestion is considered "clean" (no italic / "à vérifier" cue). */
export const TITLE_HIGH_CONFIDENCE = 0.7;

/**
 * Visual badge state derived from a suggestion. `none` means no badge at all
 * (either no suggestion, or the user has already edited the field).
 *
 *  - `clean`   : confidence >= 0.7, plain "✨ suggestion" badge, value rendered normally
 *  - `tentative`: 0.4 <= confidence < 0.7, "✨ à vérifier" badge, value rendered italic
 *  - `none`    : nothing to show
 */
export type TitleBadgeState = "none" | "clean" | "tentative";

export type SuggestionDecision = {
  /** New title to set in the field (or `null` to leave the current value unchanged). */
  nextTitle: string | null;
  /** New artist to set in the field (or `null` to leave the current value unchanged). */
  nextArtist: string | null;
  /** Badge to display next to the title label. */
  titleBadge: TitleBadgeState;
  /** Badge to display next to the artist label. */
  artistBadge: TitleBadgeState;
};

type ApplySuggestionInput = {
  response: Pick<
    InspectMidiResponse,
    "suggestedTitle" | "suggestedComposer" | "titleConfidence" | "titleSource"
  >;
  /** True when the user has manually touched the title field (incl. clearing it). */
  userHasEditedTitle: boolean;
  /** True when the user has manually touched the artist field. */
  userHasEditedArtist: boolean;
};

/**
 * Decide what the form state should become once an `inspectMidi` response
 * lands. Honors the anti-stomp contract : whenever the user has already
 * touched a field, we leave it strictly alone (no value change, no badge).
 */
export function decidePrefill(input: ApplySuggestionInput): SuggestionDecision {
  const { response, userHasEditedTitle, userHasEditedArtist } = input;
  const decision: SuggestionDecision = {
    nextTitle: null,
    nextArtist: null,
    titleBadge: "none",
    artistBadge: "none",
  };

  const confidence = response.titleConfidence ?? 0;
  const canSuggestTitle =
    !!response.suggestedTitle && confidence >= TITLE_PREFILL_MIN_CONFIDENCE;

  if (canSuggestTitle && !userHasEditedTitle) {
    decision.nextTitle = response.suggestedTitle;
    decision.titleBadge = badgeFromConfidence(confidence);
  }

  // Composer pre-fill is not gated by `titleConfidence`, the normalizer is
  // already strict about producing a composer only when it has a strong
  // identification. We still skip it when the user has typed something.
  if (response.suggestedComposer && !userHasEditedArtist) {
    decision.nextArtist = response.suggestedComposer;
    decision.artistBadge = badgeFromConfidence(confidence);
  }

  return decision;
}

function badgeFromConfidence(confidence: number): TitleBadgeState {
  if (confidence >= TITLE_HIGH_CONFIDENCE) {
    return "clean";
  }

  if (confidence >= TITLE_PREFILL_MIN_CONFIDENCE) {
    return "tentative";
  }

  return "none";
}

/** UI label for a badge. Kept here so tests can assert on the literal text. */
export function badgeLabel(state: TitleBadgeState): string | null {
  if (state === "clean") {
    return "✨ suggestion";
  }

  if (state === "tentative") {
    return "✨ à vérifier";
  }

  return null;
}

/** Tooltip used on the tentative badge, exposed for tests. */
export const TENTATIVE_BADGE_TITLE =
  "Suggestion incertaine, à vérifier avant import";

/** Re-exported for convenience in callers that already import this module. */
export type { TitleSuggestionSource };
